import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconBoxModel2, IconPlus, IconEdit, IconTrash } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const ProductModels = () => {
    const [appId, setAppId] = useState(null);
    const [models, setModels] = useState([]);
    const [brands, setBrands] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingModel, setEditingModel] = useState(null);
    const [form, setForm] = useState({ name: '', brand: '' });

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadModels();
        loadBrands();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadModels = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/product-models/`);
            setModels(res.data || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar modelos' });
        }
    };

    const loadBrands = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/brands/`);
            setBrands(res.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenCreate = () => {
        setEditingModel(null);
        setForm({ name: '', brand: '' });
        setDialogOpen(true);
    };

    const handleOpenEdit = (model) => {
        setEditingModel(model);
        setForm({ name: model.name, brand: model.brand || '' });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editingModel) {
                await api.patch(`v1/apps/${appId}/product-models/${editingModel.id}/`, form);
            } else {
                await api.post(`v1/apps/${appId}/product-models/`, form);
            }
            setDialogOpen(false);
            loadModels();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao salvar modelo' });
        }
    };

    const handleDelete = async (model) => {
        if (!window.confirm(`Excluir o modelo "${model.name}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/product-models/${model.id}/`);
            setModels((prev) => prev.filter((m) => m.id !== model.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir modelo' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconBoxModel2}
                gradient="teal"
                title="Modelos"
                subtitle="Modelos de produto, opcionalmente ligados a uma marca"
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenCreate}>
                        Novo modelo
                    </Button>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Marca</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {models.map((model) => (
                                <TableRow key={model.id} hover>
                                    <TableCell>{model.name}</TableCell>
                                    <TableCell>{model.brand_name || '—'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenEdit(model)}>
                                            <IconEdit size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(model)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {models.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        Nenhum modelo cadastrado ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingModel ? 'Editar modelo' : 'Novo modelo'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Nome" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        <TextField
                            select
                            fullWidth
                            label="Marca"
                            value={form.brand}
                            onChange={(e) => setForm({ ...form, brand: e.target.value })}
                        >
                            <MenuItem value="">Nenhuma</MenuItem>
                            {brands.map((brand) => (
                                <MenuItem key={brand.id} value={brand.id}>
                                    {brand.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProductModels;
